import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { Text_INTERNAL } from "@/react/components/shared/uikit/Typography/components/Text_INTERNAL";
import { useCommunityIconUrl } from "@/react/hooks/useCommunityIconUrl";
import { usePunditUserContext } from "@circle-react/contexts";
import { UserImage } from "@circle-react-shared/uikit/UserImage";
import { Typography } from "@circle-react-uikit/Typography";
import circleAIBotAvatar from "../assets/icons/CircleAIBot-avatar.png";
import aiIcon from "../assets/icons/ai-icon.png";
import { ActionButton } from "../components/common/ActionButton";
import { HelpInputArea } from "../components/common/HelpInputArea";
import { useCreateChat } from "../hooks/useCreateChat";

export const HomePage = () => {
  const {
    currentCommunityMember,
    currentCommunitySettings,
    isLoading: isLoadingPunditUserContext,
  } = usePunditUserContext();
  const history = useHistory();

  const communityIconUrl = useCommunityIconUrl();

  const isCopilotActionsEnabled =
    !isLoadingPunditUserContext &&
    currentCommunitySettings?.help_widget_copilot_actions_enabled;

  const { mutate: createChat } = useCreateChat({
    onSuccess: (data: { id: string }) => {
      history.push(`/conversations/${data.id}`);
    },
    onError: (error: unknown) => {
      console.error("Error creating chat", error);
    },
  });

  const handleHelpSubmit = (text: string) => {
    createChat({ text });
  };

  const onActionButtonClick = (
    type: "chat" | "feedback" | "report_bug" | "copilot_action",
  ) => {
    if (type === "chat") {
      createChat({
        text: t("help_widget.chat_bot.templates.ask_question_initial_message"),
      });
    } else if (type === "feedback") {
      createChat({
        text: t("help_widget.action_buttons.give_feedback_initial_message"),
      });
    } else if (type === "report_bug") {
      createChat({
        text: t("help_widget.action_buttons.report_bug_initial_message"),
      });
    } else if (type === "copilot_action") {
      createChat({ is_copilot: true });
    }
  };

  return (
    <div className="flex h-full min-h-[582px] flex-col items-center justify-end pb-px">
      <div className="flex w-[400px] flex-col items-center px-4">
        {/* Icons row */}
        <div className="relative flex h-16 justify-center">
          {/* AI Icon Left */}
          <div className="absolute left-[calc(50%-56px)] top-1/2 z-0 flex size-10 -translate-y-1/2 items-center justify-center">
            <img
              src={communityIconUrl || aiIcon}
              alt={t("help_widget.home_page.ai_icon_alt")}
              className="size-full rounded-full object-contain"
              loading="eager"
            />
          </div>

          {/* Circle AI Bot Avatar */}
          <div className="z-10 flex size-16 items-center justify-center">
            <img
              src={circleAIBotAvatar}
              alt={t("help_widget.home_page.circle_ai_bot_alt")}
              className="size-full object-contain"
              loading="eager"
            />
          </div>

          {/* Current User Avatar */}
          <div className="absolute right-[calc(50%-56px)] top-1/2 z-0 flex size-10 -translate-y-1/2 items-center justify-center">
            {currentCommunityMember && (
              <UserImage
                src={currentCommunityMember.avatar_url}
                name={currentCommunityMember.name}
                size="10"
              />
            )}
          </div>
        </div>

        {/* Greeting */}
        <div className="mt-4 flex items-center gap-2 text-center">
          <Text_INTERNAL
            as="span"
            color="text-gray-900 dark:text-gray-50"
            weight="font-semibold"
            size="text-2xl"
            leading="leading-[32px]"
            tracking="tracking-[0]"
            textAlign=""
          >
            {t("help_widget.home_page.greeting", {
              first_name:
                currentCommunityMember?.first_name ||
                t("help_widget.home_page.default_greeting_name"),
            })}
          </Text_INTERNAL>
        </div>
        <div className="mt-1">
          <Typography.LabelSm
            color="text-default dark:text-feature-light"
            weight="medium"
          >
            {t("help_widget.home_page.help_text")}
          </Typography.LabelSm>
        </div>

        <div className="mt-6 flex w-full flex-col items-center gap-2">
          <div className="flex w-full justify-center gap-2">
            <ActionButton
              iconName="sw-question"
              text={t("help_widget.home_page.ask_question")}
              onClick={() => onActionButtonClick("chat")}
            />
            <ActionButton
              iconName="sw-report-bug"
              text={t("help_widget.home_page.report_bug")}
              onClick={() => onActionButtonClick("report_bug")}
            />
          </div>
          <div className="flex w-full justify-center gap-2">
            <ActionButton
              iconName="sw-feedback"
              text={t("help_widget.home_page.give_feedback")}
              onClick={() => onActionButtonClick("feedback")}
            />
            {isCopilotActionsEnabled && (
              <ActionButton
                iconName="16-stars-ai"
                text={t("help_widget.home_page.perform_an_action")}
                onClick={() => onActionButtonClick("copilot_action")}
              />
            )}
          </div>
        </div>

        <div className="mt-[68px] w-full">
          <HelpInputArea
            onSubmit={handleHelpSubmit}
            placeholder={t("help_widget.home_page.help_input_placeholder")}
            allowAttachments={false}
          />
        </div>
      </div>
    </div>
  );
};
